import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { Table, Header, Form } from 'semantic-ui-react';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import ListRow from './ListRow';
import { resolveProductFamily } from '../../constants/subscriptions';

class SubscriptionsList extends Component {
  render() {
    const {
      onSelectSubscription,
      subscriptions,
      isDisable,
      customerNotes,
      dealerInfo,
      isMultiSubscriptionEnabled,
    } = this.props;
    const groupKey = sub => resolveProductFamily(sub);
    const dateValue = (value, fallback) => {
      if (isEmpty(value)) return fallback;
      const parsed = moment(value, 'YYYY-MM-DD').valueOf();
      return Number.isNaN(parsed) ? fallback : parsed;
    };
    const endValue = sub => dateValue(sub.endDate, Infinity);
    const startValue = sub => dateValue(sub.startDate, -Infinity);
    const isPreferred = (sub, best) => {
      if (endValue(sub) !== endValue(best)) return endValue(sub) > endValue(best);
      if (startValue(sub) !== startValue(best)) return startValue(sub) > startValue(best);
      return Number(sub.id) < Number(best.id);
    };

    // Rows are grouped by product family so every record of one family (including
    // product changes) stays together. Each family contributes exactly one current
    // record — the one the backend marks active; if the whole family is inactive
    // (e.g. expired), keep the latest one (by end date) enabled for re-enabling.
    const currentSubscriptionIds = (() => {
      if (!isMultiSubscriptionEnabled) return new Set();

      const groups = {};
      subscriptions.forEach(sub => {
        const key = groupKey(sub);
        if (!groups[key]) groups[key] = [];
        groups[key].push(sub);
      });

      const ids = new Set();
      Object.values(groups).forEach(group => {
        const activeRows = group.filter(sub => sub.active);
        const candidates = activeRows.length ? activeRows : group;
        const current = candidates.reduce((best, sub) => (isPreferred(sub, best) ? sub : best));
        ids.add(current.id);
      });
      return ids;
    })();

    // Within each product family the current (active) record is shown first, then
    // the history rows in the order the backend returns them. The backend
    // numbers the returned versions sequentially in `id` (active first, then
    // history from the most recent change to the oldest), so sorting the
    // history by id ascending preserves that "most recent change on top" order.
    const sortedSubscriptions = (() => {
      if (!isMultiSubscriptionEnabled) return subscriptions;
      const grouped = {};
      const orderedKeys = [];
      subscriptions.forEach(sub => {
        const key = groupKey(sub);
        if (!grouped[key]) {
          grouped[key] = [];
          orderedKeys.push(key);
        }
        grouped[key].push(sub);
      });
      return orderedKeys.reduce((acc, key) => {
        const group = grouped[key];
        const current = group.filter(sub => currentSubscriptionIds.has(sub.id));
        const rest = group
          .filter(sub => !currentSubscriptionIds.has(sub.id))
          .sort((a, b) => Number(a.id) - Number(b.id));
        return acc.concat(current, rest);
      }, []);
    })();

    const listRow = ListRow(
      onSelectSubscription,
      isDisable,
      isMultiSubscriptionEnabled,
      currentSubscriptionIds
    );

    const showDealer = () => {
      if (isEmpty(subscriptions)) {
        return '';
      }

      if (isEmpty(subscriptions[0].dealerName)) {
        return `${subscriptions[0].dealerNumber}`;
      }

      return (
        !isEmpty(subscriptions)
        && `${subscriptions[0].dealerNumber} - ${subscriptions[0].dealerName}`
      );
    };

    const showSubscriptionId = () => {
      return (
        !isEmpty(subscriptions)
        && `Subscription ID: ${subscriptions[0].subscription_id}`
      );
    };

    const showCustomerNotes = () => {
      if (!customerNotes) {
        return '';
      }
      if (
        customerNotes
        && (!customerNotes.message || isEmpty(customerNotes.message))
      ) {
        return '';
      }
      return customerNotes.message;
    };
    const isShowCustomerNotes = () => {
      if (isEmpty(showDealer())) {
        return false;
      }
      return true;
    };

    const headerStyle = {
      display: 'grid',
      justifyContent: 'space-between',
      gridTemplateColumns: 'auto auto',
    };
    const containerStyle = {
      flexDirection: 'row-reverse',
      textAlign: 'right',
    };
    const customerNotesStyle = {
      color: 'rgb(34 36 38 / 52%)',
      textAlign: 'right',
    };
    // with character length is 50 width is 360px is fine but if the length change the width will be change also
    const fieldStyle = {
      textAlign: 'right',
      width: '360px',
    };

    const headerRow = (
      <Table.Row>
        <Table.HeaderCell>Product Name</Table.HeaderCell>
        <Table.HeaderCell>Price Start Date</Table.HeaderCell>
        <Table.HeaderCell>Price DS</Table.HeaderCell>
        <Table.HeaderCell>Price PSI LPI</Table.HeaderCell>
        <Table.HeaderCell>Notify Email</Table.HeaderCell>
        <Table.HeaderCell>Network Plus Adjustment</Table.HeaderCell>
        <Table.HeaderCell>Subscription Start Date</Table.HeaderCell>
        <Table.HeaderCell>Subscription End Date</Table.HeaderCell>
        <Table.HeaderCell>Subscription Type</Table.HeaderCell>
        <Table.HeaderCell>Ds Paid PSI</Table.HeaderCell>
        <Table.HeaderCell>Discounted Pricing</Table.HeaderCell>
        <Table.HeaderCell>Options</Table.HeaderCell>
      </Table.Row>
    );

    return (
      <div>
        <div>
          <Header style={headerStyle}>
            <span>Subscriptions Manager</span>
            <span>{dealerInfo}</span>
            <span />
            <span>{showSubscriptionId()}</span>
          </Header>
          {isShowCustomerNotes() && (
            <Form style={{ paddingRight: '10px' }}>
              <Form.Group widths='two' style={containerStyle}>
                <div style={fieldStyle}>
                  <input
                    style={customerNotesStyle}
                    value={showCustomerNotes()}
                    disabled
                  />
                </div>
              </Form.Group>
            </Form>
          )}
          <Table
            celled
            selectable
            padded
            renderBodyRow={listRow}
            tableData={sortedSubscriptions}
            headerRow={headerRow}
            size='small'
            compact='very'
            style={{ fontSize: '90%' }}
            striped
            data-testid='subscriptions-table'
          />
        </div>
      </div>
    );
  }
}

SubscriptionsList.propTypes = {
  onSelectSubscription: PropTypes.func.isRequired,
  subscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      dealerName: PropTypes.string,
      dealerNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      subscription_id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    })
  ),
  isDisable: PropTypes.bool,
  customerNotes: PropTypes.shape({
    message: PropTypes.string,
  }),
  dealerInfo: PropTypes.string,
  isMultiSubscriptionEnabled: PropTypes.bool,
};

SubscriptionsList.defaultProps = {
  subscriptions: [],
  isDisable: false,
  customerNotes: null,
  dealerInfo: '',
  isMultiSubscriptionEnabled: false,
};

export default compose(withApollo, withRouter)(SubscriptionsList);
